import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "../components/ErrorNote";
import { useSession } from "../useSession";

type Entry = {
  id: string;
  patient_id: string;
  patient_name: string;
  status: "arrived" | "in_progress";
  arrived_at: string;
  waiting_minutes: number;
  seen_by_name: string;
};

function waited(minutes: number) {
  if (minutes < 1) return "just arrived";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

/**
 * Who is in the building.
 *
 * Arrival order, which is the only ordering this build offers. There are no
 * priority lanes, no ticket numbers, no display board and no estimated wait
 * (DSN-HLT-006 §10.1). A queue that reorders itself invites an argument at the
 * desk, so those limits are why it was accepted at all and this screen keeps
 * to them rather than growing toward them.
 *
 * Nothing here is clinical. A name, how long they have waited, and a state.
 * The front desk works this list and reads no chart (SCH-F-045).
 *
 * ## Read from across a desk
 *
 * This is the one screen in the portal nobody sits down to. Somebody is
 * standing, holding a telephone, and glancing over. The old version put the
 * name, the wait and the state in one line of body text at the same weight,
 * which is legible at arm's length and useless at three feet. The name is now
 * the size of a heading and the wait sits beside it in tabular figures, so the
 * column of times can be scanned down without reading any of them.
 *
 * And nothing here gates care. A doctor opens a visit from the chart whether
 * the patient was checked in or not; if they were, they leave this list on
 * their own (SCH-F-041).
 */
export function Queue() {
  const { clinic } = useSession();
  // Doctors and nurses read charts; the front desk works the queue
  // and does not (brief 02 section 10, brief 04 section 1).
  const opensCharts = clinic?.role === "doctor" || clinic?.role === "nurse";
  const [rows, setRows] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  /** Which row is being marked as gone, and the reason being typed for it. */
  const [leaving, setLeaving] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const reasonBox = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const { data, error: failed } = await api.GET("/portal/queue", {
      params: { header: clinicHeader() },
    });
    setLoading(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setError(null);
    setRows((data?.items ?? []) as Entry[]);
  }, []);

  useEffect(() => {
    void load();
    // A waiting room changes while somebody is looking at it. Thirty seconds
    // is often enough to be useful and rare enough to ignore.
    const timer = setInterval(() => void load(), 30_000);
    return () => clearInterval(timer);
  }, [load]);

  function startLeaving(row: Entry) {
    setLeaving(row.id);
    setReason("Could not wait.");
    // The box is drawn by the state change above, so focusing waits a frame.
    window.setTimeout(() => reasonBox.current?.select(), 0);
  }

  async function confirmLeft(row: Entry) {
    setBusy(row.id);
    const { error: failed } = await api.POST("/portal/queue/{id}/left", {
      params: { path: { id: row.id }, header: clinicHeader() },
      body: { reason: reason.trim() || "Left without being seen." },
    });
    setBusy(null);
    setLeaving(null);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    await load();
  }

  if (loading) return <p className="empty">Reading the waiting list…</p>;

  const waiting = rows.filter((r) => r.status === "arrived");
  const seeing = rows.filter((r) => r.status === "in_progress");

  return (
    <section className="queue">
      {/* The band brief 04's board opens with. The eyebrow names the clinic,
          because this screen is often the one left up on a wall and whoever
          walks past it should not have to work out whose waiting room they
          are looking at. */}
      <header className="screen-band">
        <div className="screen-band-photo" aria-hidden="true" />
        <div className="screen-band-words">
          {clinic?.name && (
            <p className="screen-band-eyebrow">Today at {clinic.name}</p>
          )}
          <h1>In the clinic</h1>
          <p className="screen-lead">Who is in the building, in arrival order.</p>
        </div>
      </header>

      {/* The day's state in one line, at a size somebody standing up can read.
          Not a row of KPI cards: there are two numbers and they belong in a
          sentence. */}
      <p className="queue-count">
        <strong>{waiting.length}</strong> waiting
        <span className="queue-count-sep">·</span>
        <strong>{seeing.length}</strong> in consultation
      </p>
      <p className="queue-order">Arrival order. The list never reorders itself.</p>

      <ErrorNote message={error} />

      {rows.length === 0 && (
        <p className="empty">
          Nobody is here. Patients appear as the desk checks them in.
        </p>
      )}

      {/* Column labels, as section 13 lays the screen out and the board
          draws it. Section 14 asks for this screen to be readable by somebody
          standing away from the monitor, and a column of times with nothing
          saying what they are is a column that has to be worked out. */}
      {rows.length > 0 && (
        <div className="queue-labels" aria-hidden="true">
          <span>Patient</span>
          <span>Waiting</span>
          <span>State</span>
          <span />
        </div>
      )}

      <ul className="queue-list">
        {rows.map((row) => (
          <li
            key={row.id}
            className={row.status === "in_progress" ? "queued seeing" : "queued"}
          >
            <div className="queued-who">
              {/* A link only for somebody it would open for. Brief 04 is
                  plain that the queue holds no clinical content and that
                  working it is not a route into a chart; the server has
                  always refused, so this changes nothing about what a front
                  desk can read. What it changes is being shown a door that
                  is locked — the patient list says "Needs access" rather
                  than inviting the click, and this now agrees with it. */}
              {opensCharts ? (
                <Link to={`/patients/${row.patient_id}`} className="queued-name">
                  {row.patient_name}
                </Link>
              ) : (
                <span className="queued-name">{row.patient_name}</span>
              )}
              <span className="queued-sub">
                {row.status === "in_progress"
                  ? `with ${row.seen_by_name || "a clinician"}`
                  : "waiting"}
              </span>
            </div>

            {/* Tabular figures, so a column of times lines up and can be
                scanned without being read. */}
            <div className="queued-waited">
              {row.status === "in_progress" ? "—" : waited(row.waiting_minutes)}
            </div>

            <span className={`queued-state ${row.status}`}>
              {row.status === "in_progress" ? "In consultation" : "Waiting"}
            </span>

            <div className="queued-action">
              {row.status === "arrived" && leaving !== row.id && (
                <button type="button" className="quiet" onClick={() => startLeaving(row)}>
                  Left
                </button>
              )}
            </div>

            {/* Inline, not a browser prompt. A `window.prompt` in a clinical
                workspace is an operating-system dialog with no styling, no
                label and no way to explain that this cancels rather than
                completes the visit. */}
            {leaving === row.id && (
              <form
                className="queued-left"
                onSubmit={(event) => {
                  event.preventDefault();
                  void confirmLeft(row);
                }}
              >
                <label htmlFor={`why-${row.id}`}>
                  {row.patient_name} went without being seen. Why?
                </label>
                <div className="queued-left-row">
                  <input
                    id={`why-${row.id}`}
                    ref={reasonBox}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                  />
                  <button type="submit" disabled={busy === row.id}>
                    {busy === row.id ? "Saving…" : "Mark as left"}
                  </button>
                  <button type="button" className="quiet" onClick={() => setLeaving(null)}>
                    Keep waiting
                  </button>
                </div>
                <p className="queued-left-note">
                  This cancels the visit. It does not record a consultation.
                </p>
              </form>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
